import React, { useCallback, useMemo, useState } from "react";
import {
  Alert,
  Button,
  CircularProgress,
  Link,
  Stack,
  Typography,
} from "@mui/material";
import { Dialog } from "../../../components/Dialog/Dialog";
import { ExportSettings } from "../../../components/Export/ExportSettings";
import { InstallsExportOption } from "../../../components/Export/InstallsExportOptions";
import { ContractExportOptions } from "../../../components/Export/ContractExportOptions";
import { FAIExportOptions } from "../../../components/Export/FAIExportOptions";
import { ProductsAndVendorsOptions } from "../../../components/Export/SpendSheet/ProductsAndVendorsOptions";
import { CloudMaturityOptions } from "../../../components/Export/SpendSheet/CloudMaturityOptions";
import { ITSpendOptions } from "../../../components/Export/SpendSheet/ITSpendOptions";
import { useCustomColumns } from "../../../components/Export/SpendSheet/hooks/useCustomColumns";
import { useCloudMaturity } from "../../../components/Export/SpendSheet/hooks/useCloudMaturity";
import { getLoggedUserProfile } from "../../../utils/auth";
import { ExportSettingsItems, MAX_EXPORT_COMPANIES } from "./constants";
import {
  EXPORT_DATA_TYPES,
  EXPORT_INCLUSION_TYPES,
} from "../../../components/Export/types";
import { useSnackbar } from "../../../components/Snackbar";
import {
  getExportResults,
  NIFFLER_ERROR_MESSAGES,
  NifflerErrorCode,
  NifflerExportError,
} from "../../../apis/niffler/export";
import { getExportPayload } from "../../../components/Export/utils";
import { formatNumber } from "../../../utils/numberUtils";
import { useWorkspaceStore } from "../../../store/workspace/WorkspaceStore";
import { useCombinedFilters } from "../../../hooks/useCombinedFilters";
import {
  CompanyFilter,
  InclusionMethod,
} from "../../../legacy/types/companyFilter";
import { ExportCategory } from "./types";

import { useFaiExportFilterParams } from "../../../components/Export/hooks/useFaiExportFilterParams";
import { ExportErrorDialog } from "../../Company/components/CompanyExport/ExportErrorDialog";
import { useHasCreditsEnforcement } from "../../../hooks/useHasCreditsEnforcement";
import {
  estimateExportCredits,
  EstimateCreditsDetailResponse,
} from "../../../apis/exportTrackingApi";
import { ExportCreditsAlert } from "../../../components/Export/ExportCreditsAlert";
import { useLocalStorage } from "../../../utils/useLocalStorage";
import { useLocalStorageData } from "../../../contexts/LocalStorage/hooks";
import { useScoringStore } from "../../../store/scoringStore";

interface Props {
  onClose: () => void;
}

const checkIsInstallFilterEmpty = (userFilters: CompanyFilter) => {
  const filtersArray = [
    ...(userFilters.productCategories?.ids || []),
    ...(userFilters.products || []),
    ...(userFilters.vendors || []),
    ...(userFilters.productAttributes?.ids || []),
  ];

  return filtersArray.length === 0;
};

const checkIsFaiFilterEmpty = (userFilters: CompanyFilter) => {
  const products = (userFilters.products || []).filter(
    (product) => product.inclusionMethod !== InclusionMethod.NONE_PRESENT
  );

  const flattenedProductIds = products.flatMap((product) => product.ids);
  const flattenedFaiIds = products.flatMap((product) => [
    ...(product.departmentIds || []),
    ...(product.roleIds || []),
  ]);

  return flattenedProductIds.length === 0 || flattenedFaiIds.length === 0;
};

export const ExportModal: React.FC<Props> = ({ onClose }) => {
  const companyCount = useWorkspaceStore((state) => state.companyCount);
  const hasCreditsEnforcement = useHasCreditsEnforcement();

  const { showSuccess, showError } = useSnackbar();
  const combinedFilters = useCombinedFilters();

  const {
    getProductPresenceColumns,
    getVendorPresenceColumns,
    customColumns,
    addCustomColumn,
    updateCustomColumn,
    deleteCustomColumn,
    toggleColumnSelection,
    replaceColumns,
  } = useCustomColumns();

  const { isCloudMaturityEnabled, setIsCloudMaturityEnabled } =
    useCloudMaturity();

  const [spendInclusion, setSpendInclusion] = useState<EXPORT_INCLUSION_TYPES>(
    EXPORT_INCLUSION_TYPES.ALL
  );
  const [aiSpendInclusion, setAiSpendInclusion] =
    useState<EXPORT_INCLUSION_TYPES>(EXPORT_INCLUSION_TYPES.NONE);

  const userFilters = useWorkspaceStore((state) => state.userFilters);
  const isInstallFilterEmpty = userFilters
    ? checkIsInstallFilterEmpty(userFilters)
    : false;
  const isFaiFilterEmpty = userFilters
    ? checkIsFaiFilterEmpty(userFilters)
    : false;

  const userProfile = getLoggedUserProfile();
  const localizeInstalls = useWorkspaceStore(
    (state) => state.draft?.configuration?.localizeInstalls ?? true
  );
  const workspaceWhitespace = useWorkspaceStore(
    (state) => state.draft?.configuration?.whitespace
  );
  const activeModelFamilies = useScoringStore((s) => s.activeModelFamilies);
  const scoringModels = useScoringStore((s) => s.models);

  // Use localStorage for install inclusion and products
  const [installProductIds, setInstallProductIds] = useLocalStorage<number[]>(
    "export.installProductIds",
    []
  );

  const initialInstallInclusion = useMemo(() => {
    if (installProductIds.length > 0) return EXPORT_INCLUSION_TYPES.SELECTED;
    if (isInstallFilterEmpty) return EXPORT_INCLUSION_TYPES.NONE;
    return EXPORT_INCLUSION_TYPES.FILTERS;
  }, [installProductIds.length, isInstallFilterEmpty]);

  const [installInclusion, setInstallInclusion] =
    useLocalStorageData<EXPORT_INCLUSION_TYPES>(
      "export.installInclusion",
      initialInstallInclusion
    );

  // Use localStorage for FAI inclusion and products
  const [faiProductIds, setFaiProductIds] = useLocalStorage<number[]>(
    "export.faiProductIds",
    []
  );

  const initialFaiInclusion = useMemo(() => {
    if (faiProductIds.length > 0) return EXPORT_INCLUSION_TYPES.SELECTED;
    return EXPORT_INCLUSION_TYPES.NONE;
  }, [faiProductIds.length]);

  const [faiInclusion, setFaiInclusion] =
    useLocalStorage<EXPORT_INCLUSION_TYPES>(
      "export.faiInclusion",
      initialFaiInclusion
    );

  // Keep contracts and decision makers as regular state
  const [contractsInclusion, setContractsInclusion] =
    useState<EXPORT_INCLUSION_TYPES>(EXPORT_INCLUSION_TYPES.NONE);
  const [
    includeDecisionMakersAndInfluencersOnly,
    setIncludeDecisionMakersAndInfluencersOnly,
  ] = useState(false);

  const faiExportFilterParams = useFaiExportFilterParams(
    faiInclusion,
    combinedFilters?.products || [],
    includeDecisionMakersAndInfluencersOnly
  );

  //need to apply permission filters , so only tabs which user has permission will be displayed
  const isAllowedCategory = useCallback((_category: ExportCategory) => {
    return true;
  }, []);

  //to work with active main and sub section selection for the sidebar items
  const [mainIndex, setMainIndex] = useState<number>(0);
  const [subIndex, setSubIndex] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [estimate, setEstimate] =
    useState<EstimateCreditsDetailResponse | null>(null);
  const [isEstimatingCredits, setIsEstimatingCredits] =
    useState<boolean>(false);
  const [creditsError, setCreditsError] = useState<boolean>(false);
  const [showInsufficientError, setShowInsufficientError] = useState(false);
  const [exportErrorCode, setExportErrorCode] =
    useState<NifflerErrorCode | null>(null);
  const buildExportPayload = useCallback(() => {
    const customColumns = {
      productPresence: getProductPresenceColumns(),
      vendorPresence: getVendorPresenceColumns(),
    };

    const extraFilters = { ...faiExportFilterParams };
    return getExportPayload(
      userProfile || { email: "", oktaUserId: "" },
      {
        spendInclusion,
        aiSpendInclusion,
        installInclusion,
        contractsInclusion,
        faiInclusion,
        installProductIds,
        faiProductIds,
      },
      localizeInstalls,
      combinedFilters || {},
      customColumns,
      isCloudMaturityEnabled,
      extraFilters,
      workspaceWhitespace || undefined,
      undefined,
      undefined,
      activeModelFamilies.map((family) => ({
        family,
        name: scoringModels.find((m) => m.family === family)?.name ?? family,
      }))
    );
  }, [
    getProductPresenceColumns,
    getVendorPresenceColumns,
    faiExportFilterParams,
    userProfile,
    installInclusion,
    contractsInclusion,
    faiInclusion,
    spendInclusion,
    aiSpendInclusion,
    localizeInstalls,
    combinedFilters,
    isCloudMaturityEnabled,
    installProductIds,
    faiProductIds,
    workspaceWhitespace,
    activeModelFamilies,
    scoringModels,
  ]);

  const estimateCredits = async () => {
    if (!hasCreditsEnforcement) {
      return;
    }

    setIsEstimatingCredits(true);

    try {
      const data = buildExportPayload();
      const result = await estimateExportCredits({ export_payload: data });
      setEstimate(result);
    } catch (error) {
      console.error("Failed to estimate export credits:", error);
      setCreditsError(true);
    } finally {
      setIsEstimatingCredits(false);
    }
  };

  const exportData = async () => {
    setIsLoading(true);

    const data = buildExportPayload();
    try {
      await getExportResults(data);
      showSuccess(
        `The requested report is being processed and will be delivered to your inbox ${userProfile?.email} shortly.`
      );

      // Clear product selections if not using SELECTED option
      if (installInclusion !== EXPORT_INCLUSION_TYPES.SELECTED) {
        setInstallProductIds([]);
      }
      if (faiInclusion !== EXPORT_INCLUSION_TYPES.SELECTED) {
        setFaiProductIds([]);
      }

      onClose(); //close the dialog
    } catch (error: unknown) {
      const defaultErrorMessage = "Failed to submit export request";

      if (error instanceof NifflerExportError) {
        const errorConfig =
          NIFFLER_ERROR_MESSAGES[error.code as NifflerErrorCode];

        if (errorConfig) {
          setExportErrorCode(error.code as NifflerErrorCode);
          setShowInsufficientError(true);
        } else {
          showError(defaultErrorMessage);
        }
      } else {
        showError(defaultErrorMessage);
      }

      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleNext = async () => {
    if (mainIndex >= ExportSettingsItems.length - 1) {
      exportData();
    } else {
      let subItemsCount = -1; //there is no subitems

      if (Array.isArray(ExportSettingsItems[mainIndex]?.subItems)) {
        subItemsCount =
          ExportSettingsItems[mainIndex]?.subItems.filter((item) =>
            isAllowedCategory(item as ExportCategory)
          ).length - 1;
      }

      if (subIndex < subItemsCount) {
        //we have some items ahead to process
        setSubIndex(subIndex + 1);
      } else {
        if (subIndex >= subItemsCount) {
          //we reached the end
          const nextIndex = mainIndex + 1;
          setMainIndex(nextIndex); //move to next topic
          setSubIndex(0);
          if (nextIndex === ExportSettingsItems.length - 1) {
            await estimateCredits();
          }
        }
      }
    }
  };

  const handleSkipAndBack = () => {
    if (mainIndex === 0) {
      setMainIndex(1);
      setSubIndex(0);
    } else {
      if (mainIndex > 0) {
        setMainIndex(mainIndex - 1);
        setSubIndex(0);
      }
    }
  };

  const exportCategoryComponents = useMemo(() => {
    return {
      [ExportCategory.Installs]: (
        <InstallsExportOption
          isInstallFilterEmpty={isInstallFilterEmpty}
          installInclusion={installInclusion}
          setInstallInclusion={setInstallInclusion}
          installProductIds={installProductIds}
          setInstallProductIds={setInstallProductIds}
        />
      ),
      [ExportCategory.Contracts]: (
        <ContractExportOptions
          inclusion={contractsInclusion}
          setInclusion={setContractsInclusion}
        />
      ),
      [ExportCategory.FunctionalAreaIntelligence]: (
        <FAIExportOptions
          faiDecisionMakersAndInfluencersInclusion={
            includeDecisionMakersAndInfluencersOnly
          }
          setFaiDecisionMakersAndInfluencersInclusion={
            setIncludeDecisionMakersAndInfluencersOnly
          }
          inclusion={faiInclusion}
          setInclusion={setFaiInclusion}
          isFaiFilterEmpty={isFaiFilterEmpty}
          faiProductIds={faiProductIds}
          setFaiProductIds={setFaiProductIds}
        />
      ),
      [ExportCategory.ITSpend]: (
        <ITSpendOptions
          spendInclusion={spendInclusion}
          setSpendInclusion={setSpendInclusion}
        />
      ),
      [ExportCategory.AISpend]: (
        <ITSpendOptions
          dataType={EXPORT_DATA_TYPES.AI_SPEND}
          spendInclusion={aiSpendInclusion}
          setSpendInclusion={setAiSpendInclusion}
        />
      ),
      [ExportCategory.ProductsAndVendors]: (
        <ProductsAndVendorsOptions
          {...{
            customColumns,
            addCustomColumn,
            updateCustomColumn,
            deleteCustomColumn,
            toggleColumnSelection,
            replaceColumns,
            spendInclusion,
            setSpendInclusion,
            isCloudMaturityEnabled,
            setIsCloudMaturityEnabled,
          }}
        />
      ),
      [ExportCategory.CloudMaturity]: (
        <CloudMaturityOptions
          isCloudMaturityEnabled={isCloudMaturityEnabled}
          setIsCloudMaturityEnabled={setIsCloudMaturityEnabled}
        />
      ),
    };
  }, [
    isInstallFilterEmpty,
    installInclusion,
    contractsInclusion,
    includeDecisionMakersAndInfluencersOnly,
    faiInclusion,
    isFaiFilterEmpty,
    isCloudMaturityEnabled,
    setIsCloudMaturityEnabled,
    customColumns,
    addCustomColumn,
    updateCustomColumn,
    deleteCustomColumn,
    toggleColumnSelection,
    replaceColumns,
    installProductIds,
    faiProductIds,
    spendInclusion,
    aiSpendInclusion,
    setInstallInclusion,
    setInstallProductIds,
    setFaiInclusion,
    setFaiProductIds,
    setSpendInclusion,
    setAiSpendInclusion,
  ]);

  const exportPanelContent = useMemo(() => {
    if (mainIndex == ExportSettingsItems.length - 1) {
      return (
        <Stack padding={2}>
          <Typography variant="h5" fontWeight={500} fontSize={16}>
            Export
          </Typography>
          {hasCreditsEnforcement && (
            <ExportCreditsAlert
              estimate={estimate}
              isLoading={isEstimatingCredits}
              error={creditsError}
              sx={{ mt: 1, mb: 2, mr: 3 }}
            />
          )}
          {companyCount > MAX_EXPORT_COMPANIES && (
            <Alert
              sx={{
                mt: 1,
                mb: 2,
                mr: 3,
                bgcolor: "white",
                fontSize: 12,
                border: "1px solid",
                borderColor: "warning.main",
              }}
              severity="warning"
            >
              The export will contain maximum of{" "}
              {formatNumber(MAX_EXPORT_COMPANIES)} companies
            </Alert>
          )}

          <Typography variant="subtitle1" color="textSecondary" padding={2}>
            Your report will be delivered to your inbox within a few minutes. If
            you have any questions or need additional data, please contact{" "}
            <Link href="mailto:customersupport@hginsights.com">
              customersupport@hginsights.com.
            </Link>
          </Typography>
        </Stack>
      );
    }

    // Handle any section with subItems using the component mapping
    const currentItem = ExportSettingsItems[mainIndex];
    if (currentItem?.subItems) {
      const allowedSubItems = currentItem.subItems.filter((item) =>
        isAllowedCategory(item as ExportCategory)
      );
      const selectedSubItem = allowedSubItems[subIndex] as ExportCategory;

      if (selectedSubItem && exportCategoryComponents[selectedSubItem]) {
        return exportCategoryComponents[selectedSubItem];
      }
    }

    return null;
  }, [
    mainIndex,
    hasCreditsEnforcement,
    estimate,
    isEstimatingCredits,
    creditsError,
    companyCount,
    subIndex,
    exportCategoryComponents,
    isAllowedCategory,
  ]);

  const dialogContent = useMemo(() => {
    if (isLoading) {
      return (
        <Stack
          spacing={2}
          alignItems="center"
          justifyContent="center"
          minHeight={200}
          padding={4}
        >
          <CircularProgress />
          <Typography
            variant="subtitle1"
            textAlign="center"
            color="textSecondary"
            padding={4}
          >
            Validating Export Request. You can close this window anytime and
            continue using the app. Your report will be delivered to your email
            shortly.
          </Typography>
        </Stack>
      );
    } else {
      return (
        <Stack direction="row" spacing={2} height={400}>
          <ExportSettings
            mainSelection={mainIndex}
            subSelection={subIndex}
            onMainSelect={(index) => {
              if (index === mainIndex) {
                return;
              }

              setMainIndex(index);
              if (Array.isArray(ExportSettingsItems[index].subItems)) {
                //set the first item selected
                setSubIndex(0);
              } else {
                //set the first item selected
                setSubIndex(0);
              }
            }}
            onSubSelect={(sindex) => {
              setSubIndex(sindex);
            }}
            isAllowedCategory={isAllowedCategory}
            items={ExportSettingsItems}
          />
          {exportPanelContent}
        </Stack>
      );
    }
  }, [isLoading, mainIndex, subIndex, isAllowedCategory, exportPanelContent]);
  return (
    <>
      <ExportErrorDialog
        open={showInsufficientError}
        onClose={() => {
          setShowInsufficientError(false);
          setExportErrorCode(null);
        }}
        zIndex={1000}
        title={
          exportErrorCode
            ? NIFFLER_ERROR_MESSAGES[exportErrorCode].title
            : "Error"
        }
        message={
          exportErrorCode
            ? NIFFLER_ERROR_MESSAGES[exportErrorCode].message
            : "An error occurred."
        }
      />
      <Dialog
        open={true}
        title="Company List Export"
        onClose={onClose}
        actions={
          isLoading ? (
            <Button onClick={onClose} color="secondary" variant="outlined">
              Close
            </Button>
          ) : (
            <Stack
              direction="row"
              spacing={2}
              justifyContent="space-between"
              width="100%"
              padding={2}
            >
              <Button
                data-testid="export-modal-cancel-btn"
                onClick={onClose}
                color="secondary"
                variant="outlined"
              >
                Cancel
              </Button>
              <Stack direction="row" spacing={2}>
                <Button
                  data-testid="export-modal-back-skip-btn"
                  onClick={handleSkipAndBack}
                  disabled={isLoading}
                  color="secondary"
                  variant="outlined"
                >
                  {mainIndex < ExportSettingsItems.length - 1 ? "Skip" : "Back"}
                </Button>
                <Button
                  data-testid={
                    mainIndex < ExportSettingsItems.length - 1
                      ? "export-modal-next-btn"
                      : "export-modal-final-export-btn"
                  }
                  onClick={handleNext}
                  disabled={isLoading}
                  color="primary"
                  variant="outlined"
                >
                  {mainIndex < ExportSettingsItems.length - 1
                    ? "Next"
                    : "Export"}
                </Button>
              </Stack>
            </Stack>
          )
        }
      >
        {dialogContent}
      </Dialog>
    </>
  );
};
